import { useLocation } from 'react-router-dom';

export type Locale = 'en' | 'zh-TW' | 'ja' | 'ko' | 'th';

export const LOCALES: readonly Locale[] = ['en', 'zh-TW', 'ja', 'ko', 'th'];

interface LocaleInfo {
  /** URL prefix inside TRAVO_BASE; English is unprefixed at '/travo'. */
  prefix: string;
  /** Short switcher label, written in that language. */
  label: string;
  /** Full language name in the switcher menu, written in that language. */
  name: string;
  /** BCP 47 tag for the switcher link's lang/hreflang. */
  lang: string;
  /** BCP 47 tag for the page's <html lang>, with the region where there is one; the copy files read it from here. */
  htmlLang: string;
}

export const LOCALE_INFO: Record<Locale, LocaleInfo> = {
  en: { prefix: '', label: 'EN', name: 'English', lang: 'en', htmlLang: 'en' },
  'zh-TW': { prefix: '/zh-tw', label: '繁中', name: '繁體中文', lang: 'zh-Hant', htmlLang: 'zh-Hant-TW' },
  ja: { prefix: '/ja', label: '日本語', name: '日本語', lang: 'ja', htmlLang: 'ja' },
  ko: { prefix: '/ko', label: '한국어', name: '한국어', lang: 'ko', htmlLang: 'ko' },
  th: { prefix: '/th', label: 'ไทย', name: 'ไทย', lang: 'th', htmlLang: 'th' }
};

/** Travo's pages live under this path; the site root is the Travo Labs page. */
export const TRAVO_BASE = '/travo';

const hasPrefix = (pathname: string, prefix: string): boolean =>
  prefix !== '' && (pathname === prefix || pathname.startsWith(`${prefix}/`));

/** Path inside TRAVO_BASE ('/travo/zh-tw' → '/zh-tw', '/travo' → '/'), or null outside it. */
const withinTravo = (pathname: string): string | null =>
  pathname === TRAVO_BASE || pathname.startsWith(`${TRAVO_BASE}/`) ? pathname.slice(TRAVO_BASE.length) || '/' : null;

export const isTravoPath = (pathname: string): boolean => withinTravo(pathname) !== null;

/** Travo locale of a path; English for anything outside /travo. */
export const localeFromPath = (pathname: string): Locale => {
  const inner = withinTravo(pathname);
  return (inner && LOCALES.find((locale) => hasPrefix(inner, LOCALE_INFO[locale].prefix))) || 'en';
};

/** Travo path without base and locale: '/travo/zh-tw/about-us' → '/about-us', '/travo/ja' → '/'. */
export const stripLocale = (pathname: string): string =>
  (withinTravo(pathname) ?? pathname).slice(LOCALE_INFO[localeFromPath(pathname)].prefix.length) || '/';

/** Full Travo URL path for an unlocalized path ('/', '/about-us', '/#features') in a locale. */
export const localizePath = (locale: Locale, path: string): string => {
  const { prefix } = LOCALE_INFO[locale];
  if (path === '/') return TRAVO_BASE + prefix;
  return `${TRAVO_BASE}${prefix}${path.startsWith('/#') ? path.slice(1) : path}`;
};

export const useLocale = (): Locale => localeFromPath(useLocation().pathname);
